import React, { useState } from 'react'
import { ColorResult, SketchPicker } from 'react-color'
import { Grid } from '@mui/material'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'

import Toggle from '../icons/Toggle'
import { Field } from '../types'
import PuiCheckbox from './inputs/PuiCheckbox'
import PuiPopper from './PuiPopper'
import Text from './Text'

const useStyles = makeStyles(
  theme => ({
    root: {},
    swatch: {
      width: theme.spacing(8),
      height: theme.spacing(3),
      marginLeft: theme.spacing(1),
      padding: 2,
      paddingRight: 0,
      background: theme.colors.tableBackground,
      borderRadius: 4,
      border: theme.constants.tableBorder,
      cursor: 'pointer',
    },
    swatchDisabled: {
      paddingRight: 2,
      cursor: 'auto',
    },
    color: {
      width: '100%',
      height: '100%',
      borderRadius: 3,
      backgroundColor: (props: UseStylesProps) => props.color,
    },
    noColor: {
      width: '100%',
      height: '100%',
      borderRadius: 3,
      background:
        'linear-gradient(340deg, rgba(223,223,223,1) 46%, rgba(228,0,0,1) 50%, rgba(223,223,223,1) 54%)',
    },
    toggleIcon: {
      color: theme.colors.sideText,
    },
    checkbox: {
      textAlign: 'center',
    },
  }),
  { name: 'ColorPicker' },
)

export interface ColorPickerProps {
  readonly checkBoxLabel?: string
  readonly className?: string
  readonly disabled?: boolean
  readonly field: Field
  readonly label?: React.ReactNode
  readonly noColorValueEnabled?: boolean
  readonly onCheckboxClick?: (field: Field) => void
}

interface UseStylesProps {
  color: Field['value']
}

const ColorPicker = ({
  className,
  label,
  field,
  noColorValueEnabled,
  checkBoxLabel,
  onCheckboxClick,
  disabled,
}: ColorPickerProps) => {
  const useStylesProps: UseStylesProps = {
    color: field.value,
  }
  const classes = useStyles(useStylesProps)

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement>()

  const onSwatchClick = ({
    currentTarget,
  }: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(anchorEl ? undefined : currentTarget)
  }

  const onColorChange = (color: ColorResult) => {
    field.setValue(color.hex)
  }

  const removeColor = () => {
    field.setValue('')
    setAnchorEl(undefined)
  }

  return (
    <Grid container item className={classNames(className, classes.root)}>
      {label && <Text variant="body2">{label}</Text>}
      <Grid
        container
        item
        alignItems="center"
        className={classNames(classes.swatch, {
          [classes.swatchDisabled]: disabled,
        })}
        wrap="nowrap"
        onClick={disabled ? undefined : onSwatchClick}
      >
        <Grid
          item
          className={
            !field.value && noColorValueEnabled
              ? classes.noColor
              : classes.color
          }
        />
        {!disabled && <Toggle className={classes.toggleIcon} />}
      </Grid>
      <PuiPopper
        hideCloseButton
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        placement="bottom"
        onClose={() => setAnchorEl(undefined)}
      >
        <Grid p={1}>
          <SketchPicker color={field.value} onChange={onColorChange} />
          {checkBoxLabel && field.value && (
            <Grid className={classes.checkbox}>
              <PuiCheckbox
                checked={!field.value}
                label={checkBoxLabel}
                onChange={() =>
                  onCheckboxClick ? onCheckboxClick(field) : removeColor()
                }
              />
            </Grid>
          )}
        </Grid>
      </PuiPopper>
    </Grid>
  )
}

export default ColorPicker
